/**
 * ContractsPage — `/contratos`: os contratos com assinatura digital da empresa.
 *
 * Módulo PRO (portão de PLANO, `contracts`), sem gate de papel.
 *
 * ─── O DESENHO OFICIAL NUMA LISTA DE CONTRATOS ──────────────────────────────
 *
 * Hero sangrado + bilhete, o mesmo de `/quotes`, `/ordens-servico` e
 * `/recibos`. O que precisou ser TRADUZIDO aqui (o exercício do
 * #182/#188/#195/#197/#198/#199):
 *
 * · O EYEBROW diz o "onde" — a família de que a lista faz parte.
 * · O VALOR AO VIVO é a soma do recorte visível, e a regra de quem entra nela é
 *   de NEGÓCIO: contrato CANCELADO é negócio que não vai acontecer e fica FORA;
 *   RASCUNHO fica DENTRO (é dinheiro que ainda pode entrar, o análogo do
 *   orçamento pendente). O que ficou de fora é contado na sublinha ("no silent
 *   caps"). Mora no módulo puro `contractsListFilters`.
 * · O BOTÃO DE VOLTAR fica, como em `/ordens-servico` e `/recibos`:
 *   `/contratos` NÃO é destino da navegação inferior, então sem ele não há
 *   saída barata da tela.
 * · O STATUS deixou de ser `<Tabs>` e virou faixa de botões planos (#25/#188):
 *   status é RECORTE, não troca de CONTEXTO — e como esta tela não tem nenhuma
 *   troca de contexto, não sobrou `<Tabs>` nenhum. São SEIS botões, a faixa mais
 *   longa do app, e ela ROLA de propósito (ver o módulo puro).
 *
 * O que NÃO transferir: modo de seleção e exportação. As duas existem em
 * `/quotes` e `/recibos` porque lá há ação em massa e planilha; aqui não há
 * nenhuma das duas, e copiar o ícone "porque o molde tem" seria inventar
 * funcionalidade no meio de um redesenho (#198).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  ArrowLeft,
  ArrowUpDown,
  Check,
  Crown,
  FileSignature,
  Plus,
  Search,
  SlidersHorizontal,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import { useContracts, contractStatusLabel, type ContractStatus } from '@/hooks/useContracts';
import {
  CONTRACT_PERIOD_PRESETS,
  CONTRACT_SORT_LABELS,
  CONTRACT_SORT_ORDER,
  CONTRACT_STATUS_FILTER_OPTIONS,
  DEFAULT_CONTRACT_SORT,
  contractListTotalLabel,
  contractMatchesSearch,
  contractStatusOf,
  countActiveContractFilters,
  countContractsByStatus,
  isDeadContractStatusFilter,
  matchesContractPeriod,
  matchesContractStatus,
  matchesContractValue,
  sortContractsBy,
  summarizeContractList,
  type ContractSortOption,
  type ContractStatusFilter,
} from '@/lib/contractsListFilters';
import {
  FILTER_CHIP_COMPACT,
  FILTER_CHIP_OFF,
  FILTER_CHIP_ON,
  FILTER_CHIP_ROW,
} from '@/components/filters/filterChips';
import { ListFiltersDialog, type FilterGroup } from '@/components/filters/ListFiltersDialog';
import { EMPTY_LIST_RANGE, type ListValueRange } from '@/lib/listFilters';
import { PERIOD_PRESET_LABELS, resolvePeriodPreset, type ReportPeriodPreset } from '@/lib/reportPeriod';
import { CONTENT_CARD } from '@/components/ui/field';
import { heroTotalSizeClass } from '@/lib/quoteHero';
import { plural } from '@/lib/plural';
import { cn } from '@/lib/utils';

const STATUS_STYLES: Record<ContractStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  sent: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  partially_signed: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
  signed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
  cancelled: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
};

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const PERIOD_GROUP_KEY = 'period';

export default function ContractsPage() {
  const navigate = useNavigate();
  // Só LEITURA: o atalho `?new=true` navega e não reescreve a URL (ver abaixo).
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const queryClient = useQueryClient();
  const contractsFeature = useFeature('contracts');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ContractStatusFilter>('all');
  const [sortOption, setSortOption] = useState<ContractSortOption>(DEFAULT_CONTRACT_SORT);
  const [periodPreset, setPeriodPreset] = useState<ReportPeriodPreset | null>(null);
  const [valueRange, setValueRange] = useState<ListValueRange>(EMPTY_LIST_RANGE);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { contracts, isLoading } = useContracts();

  /**
   * Atalho "Contrato" da navegação inferior (`/contratos?new=true`).
   *
   * 🔴 Aqui NÃO se limpa o parâmetro depois de navegar, e isso é a correção de
   * um defeito: `setSearchParams` navega para a rota CORRENTE sem o parâmetro,
   * então chamá-lo logo depois do `navigate` desfazia a navegação — o atalho
   * deixava o usuário parado em `/contratos`, sem formulário nenhum e sem erro.
   * Como o `navigate` é `replace`, a entrada com `?new=true` some do histórico
   * de qualquer jeito. Limpar a URL só faz sentido quando a ação abre um MODAL
   * na própria rota (é o caso de `/recibos` e `/agenda`).
   */
  useEffect(() => {
    if (user && contractsFeature.allowed && searchParams.get('new') === 'true') {
      navigate('/contratos/novo', { replace: true });
    }
  }, [user, contractsFeature.allowed, searchParams, navigate]);

  // Refetch when an offline write (or another tab) touches contract data.
  useEffect(() => {
    const handler = () => {
      queryClient.invalidateQueries({ queryKey: ['contracts', activeCompany?.id] });
    };
    window.addEventListener('contracts-updated', handler);
    return () => window.removeEventListener('contracts-updated', handler);
  }, [queryClient, activeCompany?.id]);

  /**
   * A faixa do período é resolvida UMA vez por render, e não dentro do
   * `filter`: `resolvePeriodPreset` cria `Date`s a partir de "agora", e chamá-la
   * por contrato compararia cada linha contra um "agora" diferente.
   */
  const periodRange = useMemo(
    () => (periodPreset ? resolvePeriodPreset(periodPreset) : null),
    [periodPreset],
  );

  /** O recorte SEM o status — é ele que alimenta as contagens dos botões. */
  const contractsBeforeStatus = useMemo(
    () =>
      (contracts ?? []).filter(
        (contract: any) =>
          contractMatchesSearch(contract, search) &&
          matchesContractPeriod(contract, periodRange) &&
          matchesContractValue(contract, valueRange),
      ),
    [contracts, search, periodRange, valueRange],
  );

  const statusCounts = useMemo(
    () => countContractsByStatus(contractsBeforeStatus),
    [contractsBeforeStatus],
  );

  const visible = useMemo(
    () =>
      sortContractsBy(
        contractsBeforeStatus.filter((contract: any) =>
          matchesContractStatus(contract, statusFilter),
        ),
        sortOption,
      ),
    [contractsBeforeStatus, statusFilter, sortOption],
  );

  /**
   * `includeDead` liga quando o recorte É o status morto: com o filtro em
   * "Cancelados", excluir todo mundo faria o hero anunciar R$ 0,00 com a lista
   * cheia embaixo.
   */
  const summary = summarizeContractList(visible, {
    includeDead: isDeadContractStatusFilter(statusFilter),
  });
  const activeModalFilters = countActiveContractFilters(periodPreset, valueRange);
  const recorteAtivo =
    search.trim().length > 0 || statusFilter !== 'all' || activeModalFilters > 0;

  const filterGroups: FilterGroup[] = [
    {
      key: PERIOD_GROUP_KEY,
      label: 'Criado em',
      options: [
        // "Qualquer data" é uma opção EXPLÍCITA, e não a ausência de seleção:
        // sem ela, quem escolheu um período não teria como voltar atrás.
        { value: '', label: 'Qualquer data' },
        ...CONTRACT_PERIOD_PRESETS.map((preset) => ({
          value: preset,
          label: PERIOD_PRESET_LABELS[preset],
        })),
      ],
    },
  ];

  if (!user) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <FileSignature className="mb-4 h-16 w-16 text-muted-foreground/50" />
          <h2 className="text-xl font-semibold">Contratos</h2>
          <p className="mb-4 text-muted-foreground">Faça login para acessar os contratos</p>
          <Button3D onClick={() => navigate('/auth')}>Entrar</Button3D>
        </div>
      </MainLayout>
    );
  }

  // PRO gate: contracts is a paid feature. Free plan sees an upsell.
  if (!contractsFeature.allowed) {
    return (
      <MainLayout>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-500 shadow-lg">
              <FileSignature className="h-7 w-7 text-white" />
            </div>
            <p className="font-semibold">Contratos com assinatura digital</p>
            <p className="mt-1 max-w-xs text-sm text-muted-foreground">
              Feche o negócio com um contrato assinado pelo cliente à distância, direto pelo link. Recurso
              do plano PRO.
            </p>
            <Button3D color="amber" className="mt-5" onClick={() => contractsFeature.require()}>
              <Crown className="mr-1.5 h-4 w-4" />
              Conhecer o PRO
            </Button3D>
            <Button variant="outline" className="mt-3" onClick={() => navigate('/dashboard')}>
              Voltar ao início
            </Button>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="pb-4">
        {/* ================================================================== */}
        {/* HERO — o desenho oficial do app                                     */}
        {/* ================================================================== */}
        <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
          <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
          <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

          <div className="relative flex items-center gap-2">
            {/* `/contratos` não está na navegação inferior: sem este botão, a
                única saída da tela é o gesto do sistema. */}
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
              onClick={() => navigate('/dashboard')}
              aria-label="Voltar"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] uppercase tracking-wide text-white/70">Documentos</p>
              <h1 className="line-clamp-2 text-lg font-bold leading-tight">Contratos</h1>
            </div>
            {/* A ação PRIMÁRIA mantém o texto — é ela que o excedente comeria
                primeiro se tudo fosse texto, e é a que menos pode sumir (#186). */}
            <Button3D
              size="sm"
              color="light"
              className="shrink-0"
              onClick={() => navigate('/contratos/novo')}
              aria-label="Novo contrato"
              title="Novo contrato"
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Novo
            </Button3D>
          </div>

          <div className="relative mt-5 min-w-0">
            <p className="text-xs text-white/75">{contractListTotalLabel(statusFilter)}</p>
            {/* `heroTotalSizeClass` é a FONTE ÚNICA do degrau de tamanho
                (#159-b): escrever uma regra local aqui seria mais uma cópia de
                uma medida que já divergiu entre telas uma vez. */}
            <p
              className={cn(
                'truncate font-bold leading-tight',
                heroTotalSizeClass(formatCurrency(summary.total)),
              )}
            >
              {formatCurrency(summary.total)}
            </p>
            <p className="mt-0.5 text-xs text-white/70">
              {/* Enquanto a lista carrega, o hero NÃO afirma que está vazia: um
                  "Nenhum contrato" que aparece antes da resposta é uma afirmação
                  falsa em cima de um R$ 0,00 igualmente falso (#199). */}
              {summary.count === 0
                ? isLoading
                  ? 'Carregando…'
                  : 'Nenhum contrato neste filtro'
                : plural(summary.count, 'contrato', 'contratos')}
              {/* "no silent caps": o que ficou fora da soma é DITO. Sem isto o
                  total apareceria menor do que a lista sugere, sem explicação. */}
              {summary.excludedCount > 0 &&
                ` · ${plural(
                  summary.excludedCount,
                  'cancelado fora da conta',
                  'cancelados fora da conta',
                )}`}
            </p>
          </div>
        </div>

        {/* Conteúdo — sobe e encosta na curva do hero, como em /quotes. */}
        <div className="relative z-10 -mt-9 space-y-5">
          {/* BLOCO DE CONTROLES DA LISTA — busca, recorte e ordenação ativa.
              Eles são um grupo só (todos respondem "o que eu estou vendo"),
              então ficam juntos com `space-y-2` em vez do `space-y-5` que separa
              as SEÇÕES da tela (#191/#194). */}
          <div className="space-y-2">
            {/* Busca + ordenação + filtros. Os dois ícones são `shrink-0` e o
                campo é `min-w-0 flex-1`: sem isso o input empurraria os botões
                para fora da tela em vez de encolher (#186). */}
            <div className="flex items-center gap-2">
              <div className="relative min-w-0 flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Cliente, título ou #0001"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Ordenação: menu, não modal — é uma escolha única entre rótulos
                  curtos, e um diálogo seria dois cliques a mais pelo mesmo
                  resultado. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0 rounded-xl"
                    aria-label={`Ordenar (${CONTRACT_SORT_LABELS[sortOption]})`}
                    title={`Ordenar: ${CONTRACT_SORT_LABELS[sortOption]}`}
                  >
                    <ArrowUpDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {CONTRACT_SORT_ORDER.map((option) => (
                    <DropdownMenuItem
                      key={option}
                      onSelect={() => setSortOption(option)}
                      className="gap-2"
                    >
                      {/* O CHECK ocupa o mesmo espaço quando ausente: sem o
                          placeholder, o rótulo escolhido fica deslocado. */}
                      <span className="flex h-4 w-4 items-center justify-center">
                        {sortOption === option && <Check className="h-4 w-4 text-primary" />}
                      </span>
                      {CONTRACT_SORT_LABELS[option]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Filtros: aqui SIM um modal — são controles independentes que
                  fazem sentido escolhidos juntos, e o rascunho do diálogo é o
                  que impede a lista de se reorganizar a cada tecla do campo de
                  valor. */}
              <Button
                variant="outline"
                size="icon"
                className="relative h-10 w-10 shrink-0 rounded-xl"
                onClick={() => setFiltersOpen(true)}
                aria-label={
                  activeModalFilters > 0
                    ? `Filtros (${plural(activeModalFilters, 'ativo', 'ativos')})`
                    : 'Filtros'
                }
                title="Filtros"
              >
                <SlidersHorizontal className="h-4 w-4" />
                {activeModalFilters > 0 && (
                  <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold leading-none text-white">
                    {activeModalFilters}
                  </span>
                )}
              </Button>
            </div>

            {/* STATUS — botões planos, não alternador (#25/#188). A contagem vai
                no `title`/`aria-label`, onde não custa pixel (#191). */}
            <div className={FILTER_CHIP_ROW}>
              {CONTRACT_STATUS_FILTER_OPTIONS.map((option) => {
                const active = statusFilter === option.value;
                // A contagem responde "quantos eu veria se clicasse aqui" — por
                // isso sai do recorte SEM o status. Um número que não bate com a
                // lista depois do clique é pior do que número nenhum.
                const count = statusCounts[option.value] ?? 0;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setStatusFilter(option.value)}
                    aria-pressed={active}
                    aria-label={`${option.label} (${count})`}
                    title={`${option.label}: ${plural(count, 'contrato', 'contratos')}`}
                    className={cn(FILTER_CHIP_COMPACT, active ? FILTER_CHIP_ON : FILTER_CHIP_OFF)}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>

            {/* A ORDENAÇÃO ativa é a única decisão da tela que não se anuncia
                sozinha: status tem botões e período/valor têm o selo do ícone,
                mas "por que este contrato está no topo?" só se responde abrindo
                o menu. É RÓTULO, não botão — o controle é o ícone logo acima, e
                um segundo gatilho para a mesma coisa dividiria o alvo do toque. */}
            {!isLoading && visible.length > 0 && (
              <div className="flex items-center gap-1.5 px-1 text-sm text-muted-foreground">
                <ArrowUpDown className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{CONTRACT_SORT_LABELS[sortOption]}</span>
              </div>
            )}

            {/* Lista — DENTRO do mesmo grupo `space-y-2` dos controles, e não um
                `-mt-N` competindo com o `space-y-5` de fora: margem negativa num
                filho direto PERDE a briga de especificidade contra o `space-y-*`
                do container (#194). */}
            {isLoading && visible.length === 0 ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className={cn(CONTENT_CARD, 'h-24 animate-pulse')} />
                ))}
              </div>
            ) : visible.length === 0 ? (
              <div
                className={cn(
                  CONTENT_CARD,
                  'flex flex-col items-center justify-center border-dashed py-8 text-center',
                )}
              >
                <FileSignature className="mb-3 h-12 w-12 text-muted-foreground/50" />
                <p className="font-medium">
                  {recorteAtivo ? 'Nenhum contrato encontrado' : 'Nenhum contrato'}
                </p>
                <p className="mt-1 max-w-xs text-sm text-muted-foreground">
                  {recorteAtivo
                    ? 'Nenhum contrato corresponde à busca e aos filtros escolhidos.'
                    : 'Crie um contrato a partir de um orçamento ou do zero, assine e envie para o cliente assinar pelo link.'}
                </p>
                {!recorteAtivo && (
                  <Button3D className="mt-5" onClick={() => navigate('/contratos/novo')}>
                    <Plus className="mr-1.5 h-4 w-4" />
                    Novo contrato
                  </Button3D>
                )}
              </div>
            ) : (
              /* Duas colunas no desktop (padrão de `/quotes`), com
                 `grid-cols-1` explícito no celular (#185). */
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
                {visible.map((c: any) => {
                  const st = contractStatusOf(c);
                  return (
                    <div
                      key={c.id}
                      className={cn(
                        CONTENT_CARD,
                        'flex cursor-pointer items-center gap-3 p-4 transition-shadow hover:shadow-md',
                      )}
                      onClick={() => navigate(`/contratos/${c.id}`)}
                    >
                      {/* Coluna da ESQUERDA: identificação + conteúdo. Ela é
                          quem dita a altura do card — o `items-center` do card
                          faz o bloco valor+status centrar contra ELA, e não
                          contra a linha do título (#189). */}
                      <div className="min-w-0 flex-1 space-y-2">
                        {/* LINHA DE IDENTIFICAÇÃO: número, data e selos. Tudo
                            que identifica o contrato (mas não é o conteúdo dele)
                            mora aqui, o que devolve a largura inteira ao título
                            na linha de baixo. */}
                        <div className="flex flex-wrap items-center gap-2">
                          {c._offline_pending && (
                            <Badge variant="outline" className="h-5 px-1.5 py-0 text-[10px]">
                              Offline
                            </Badge>
                          )}
                          {/* O número aparece SEMPRE que existe (como em
                              `/quotes`), e não só quando `show_sequence_id`
                              está ligado: essa coluna decide o que sai no
                              DOCUMENTO, e escondê-lo aqui tornaria a busca por
                              `#0001` uma função que ninguém descobre. */}
                          {c.sequence_id && (
                            <Badge
                              variant="outline"
                              className="h-5 border-primary/30 bg-primary/5 px-1.5 py-0 font-mono text-xs text-primary"
                            >
                              #{String(c.sequence_id).padStart(4, '0')}
                            </Badge>
                          )}
                          {/* `created_at` é TIMESTAMP: formatar pelo calendário
                              LOCAL. Cortar a string no 'T' daria a data em UTC e
                              imprimiria o DIA SEGUINTE para tudo criado depois
                              das 21h (#114). */}
                          {c.created_at && (
                            <Badge
                              variant="outline"
                              className="h-5 px-1.5 py-0 text-xs font-normal text-muted-foreground"
                            >
                              {new Date(c.created_at).toLocaleDateString('pt-BR')}
                            </Badge>
                          )}
                          {st === 'signed' && c.client_signed_at && (
                            <Badge
                              variant="outline"
                              className="h-5 px-1.5 py-0 text-xs font-normal text-muted-foreground"
                            >
                              Assinado em {new Date(c.client_signed_at).toLocaleDateString('pt-BR')}
                            </Badge>
                          )}
                        </div>

                        {/* `min-w-0` não é decoração (#175): sem ele um flex
                            item nasce com `min-width: auto` e um título sem
                            espaços estica a linha para fora da viewport. E sem
                            `truncate`: é o título que distingue duas linhas da
                            mesma lista, e cortá-lo come justamente o que
                            informa. */}
                        <div className="min-w-0 space-y-0.5">
                          <p className="min-w-0 break-words font-bold">
                            {c.title || c.client_name || 'Contrato'}
                          </p>
                          {c.title && c.client_name && (
                            <p className="break-words text-sm text-muted-foreground">
                              {c.client_name}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Valor + status são UM CONJUNTO, e por isso um bloco só:
                          centrados entre si no eixo horizontal e centrados na
                          ALTURA DO CARD pelo `items-center` do card (#189). */}
                      <div className="flex shrink-0 flex-col items-center gap-1">
                        <p className="font-semibold">{formatCurrency(Number(c.total ?? 0))}</p>
                        <Badge className={cn('border-0', STATUS_STYLES[st])}>
                          {contractStatusLabel(st)}
                        </Badge>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filtros: quando o contrato foi criado e a faixa de valor.
          O STATUS aparece só na faixa de botões, e não aqui, porque ele já se
          anuncia sozinho na tela — repeti-lo no modal duplicaria o controle sem
          revelar nada que o usuário não esteja vendo. */}
      <ListFiltersDialog
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        description="Recorte os contratos por data de criação e por valor."
        groups={filterGroups}
        rangeLabel="Valor do contrato"
        value={{ groups: { [PERIOD_GROUP_KEY]: periodPreset ?? '' }, range: valueRange }}
        onApply={(next) => {
          const wanted = next.groups[PERIOD_GROUP_KEY];
          setPeriodPreset(
            wanted && CONTRACT_PERIOD_PRESETS.includes(wanted as ReportPeriodPreset)
              ? (wanted as ReportPeriodPreset)
              : null,
          );
          setValueRange(next.range);
        }}
      />
    </MainLayout>
  );
}
